import { useState } from "react";

import { useTranslation } from "react-i18next";

import { USER_INFO_LENGTHS } from "../../../shared";
import { FieldError, PasswordField } from "../types";

const emptyError: FieldError = { key: null, params: {} };

const usePasswordValidation = () => {
  const { t } = useTranslation();

  const [passwordRequirements, setPasswordRequirements] = useState({
    uppercase: false,
    lowercase: false,
    length: false
  });

  const [passwordErrorKeys, setPasswordErrorKeys] = useState<
    Record<PasswordField, FieldError>
  >({
    password1: emptyError,
    password2: emptyError
  });

  const setPasswordError = (
    field: PasswordField,
    key: string,
    params: Record<string, unknown> = {}
  ) => {
    setPasswordErrorKeys(prev => ({ ...prev, [field]: { key, params } }));
  };

  const clearPasswordError = (field: PasswordField) => {
    setPasswordErrorKeys(prev => ({ ...prev, [field]: emptyError }));
  };

  const validatePasswordCreation = (password: string) => {
    setPasswordRequirements({
      uppercase: Boolean(/[A-Z]/.test(password)),
      lowercase: Boolean(/[a-z]/.test(password)),
      length: password.length >= USER_INFO_LENGTHS.PswMin
    });
  };

  const validatePasswordMatching = (password1: string, password2: string) => {
    if (password1 !== password2) {
      setPasswordError("password2", "Auth##passwords do not match");
    }
  };

  const validatePassword = (passwordName: string) => {
    const isValid = !Object.values(passwordRequirements).includes(false);
    if (passwordName.length > 0 && !isValid) {
      setPasswordError("password1", "Auth##invalid password format");
    }
  };

  const validatePasswordLength = (
    e: any,
    setError: (key: string, params?: Record<string, unknown>) => void
  ) => {
    const inputValue = e.target.value;
    if (inputValue.length > USER_INFO_LENGTHS.PswMax) {
      setError("Password##password too long", {
        maxNumber: USER_INFO_LENGTHS.PswMax
      });
    }
  };

  const passwordError = {
    password1: passwordErrorKeys.password1.key
      ? t(passwordErrorKeys.password1.key, passwordErrorKeys.password1.params)
      : null,
    password2: passwordErrorKeys.password2.key
      ? t(passwordErrorKeys.password2.key, passwordErrorKeys.password2.params)
      : null
  };

  return {
    passwordRequirements,
    passwordError,
    passwordErrorKeys,
    setPasswordError,
    validatePasswordCreation,
    validatePasswordMatching,
    validatePassword,
    clearPasswordError,
    validatePasswordLength
  };
};

export default usePasswordValidation;
